import React, { useCallback, useEffect, useRef, useState } from "react";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Button, Stack, Typography } from "@mui/material";
import WorkspaceEmptyState from "assets/images/WorkspaceEmptyState.png";

const DEFAULT_MESSAGES = [
  "Reading your document...",
  "Analyzing deal structure...",
  "Extracting key information...",
  "Identifying asset details...",
  "Processing financial data...",
  "Mapping characteristics...",
  "Preparing your form..."
];

const STEP_INTERVAL = 2200;

interface AiLoadingStateProps {
  isLoading: boolean;
  messages?: string[];
  error?: string | null;
  onBack?: () => void;
}

export const AiLoadingState: React.FC<AiLoadingStateProps> = ({
  isLoading,
  messages = DEFAULT_MESSAGES,
  error,
  onBack
}) => {
  const [activeStep, setActiveStep] = useState(-1);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const scheduleNext = useCallback(
    (step: number) => {
      if (step >= messages.length) return;
      timerRef.current = setTimeout(() => {
        setActiveStep(step);
        scheduleNext(step + 1);
      }, STEP_INTERVAL);
    },
    [messages.length]
  );

  useEffect(() => {
    if (!isLoading) return;

    setActiveStep(0);
    scheduleNext(1);

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [isLoading, scheduleNext]);

  // Stop advancing on error
  useEffect(() => {
    if (error) {
      clearTimeout(timerRef.current);
    }
  }, [error]);

  if (!isLoading && !error) return null;

  return (
    <Stack
      sx={{
        flexGrow: 1,
        alignItems: "center",
        justifyContent: "center",
        gap: "24px",
        padding: "32px"
      }}
    >
      {/* Illustration */}
      <Stack alignItems="center">
        <Box sx={{ height: "118px", width: "190px", overflow: "hidden" }}>
          <Box
            component="img"
            src={WorkspaceEmptyState}
            sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
          />
        </Box>
      </Stack>

      {/* File name */}
      {/* Steps list */}
      <Stack sx={{ gap: "6px", width: "100%", maxWidth: "190px", display: error ? "none" : "flex" }}>
        {messages.map((msg, i) => {
          const isFailed = !!error && i === activeStep;
          const isCompleted = i < activeStep;
          const isActive = i === activeStep && !error;
          const isHidden = i > activeStep;

          return (
            <Stack
              key={i}
              direction="row"
              alignItems="center"
              sx={{
                gap: "10px",
                height: "28px",
                opacity: isHidden ? 0 : 1,
                transform: isHidden ? "translateY(6px)" : "translateY(0)",
                transition: "opacity 0.4s ease, transform 0.4s ease"
              }}
            >
              {/* Indicator */}
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {isFailed ? (
                  <ErrorOutlineIcon
                    sx={{
                      fontSize: 18,
                      color: "#d32f2f",
                      animation: "aiStepCheckIn 0.3s ease-out",
                      "@keyframes aiStepCheckIn": {
                        "0%": { transform: "scale(0.5)", opacity: 0 },
                        "100%": { transform: "scale(1)", opacity: 1 }
                      }
                    }}
                  />
                ) : isCompleted ? (
                  <CheckCircleOutlineIcon
                    sx={{
                      fontSize: 18,
                      color: "#26a6ae",
                      animation: "aiStepCheckIn 0.3s ease-out",
                      "@keyframes aiStepCheckIn": {
                        "0%": { transform: "scale(0.5)", opacity: 0 },
                        "100%": { transform: "scale(1)", opacity: 1 }
                      }
                    }}
                  />
                ) : isActive ? (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      border: "2px solid #26a6ae",
                      borderTopColor: "transparent",
                      animation: "aiStepSpin 0.8s linear infinite",
                      "@keyframes aiStepSpin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" }
                      }
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "#dbe8f5"
                    }}
                  />
                )}
              </Box>

              {/* Label */}
              <Typography
                noWrap
                sx={{
                  fontSize: "13px",
                  lineHeight: "16px",
                  color: isFailed ? "#d32f2f" : isCompleted ? "#26a6ae" : isActive ? "#071f35" : "#96a8bb",
                  fontWeight: isActive || isFailed ? 500 : 400,
                  transition: "color 0.3s ease, font-weight 0.3s ease"
                }}
              >
                {msg}
              </Typography>
            </Stack>
          );
        })}
      </Stack>

      {/* Error message + back button */}
      {error && (
        <Stack
          sx={{
            alignItems: "center",
            gap: "12px",
            animation: "aiStepCheckIn 0.3s ease-out",
            "@keyframes aiStepCheckIn": {
              "0%": { transform: "scale(0.5)", opacity: 0 },
              "100%": { transform: "scale(1)", opacity: 1 }
            }
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              lineHeight: "16px",
              color: "#496682",
              textAlign: "center",
              maxWidth: "300px"
            }}
          >
            {error}
          </Typography>
          {onBack && (
            <Button
              variant="outlined"
              onClick={onBack}
              sx={{
                textTransform: "none",
                borderRadius: "32px",
                fontSize: "12px",
                lineHeight: "16px",
                color: "#071f35",
                borderColor: "#7b92a9",
                px: "16px",
                py: "4px",
                "&:hover": { borderColor: "#496682", bgcolor: "#f6f8fc" }
              }}
            >
              Continue
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
};
